import { ExternalLink, FileText, Mail, Printer, Trash2 } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type {
  CorrespondencePackageSchema,
  RecipientEntityType,
} from '@/services/api/correspondenceService';
import {
  useCorrespondencePackageDestroyMutation,
  useCorrespondencePackageListQuery,
} from '@/services/api/correspondenceService';

import { PACKAGE_TYPE_DISPLAY } from './header';
import { STATUS_DISPLAY } from './utils/status';

// ============================================================================
// Types
// ============================================================================

interface CorrespondenceHistoryCardProps {
  matterId: string;
  /** Type of recipient entity */
  recipientEntityType: RecipientEntityType;
  /** ID of the recipient entity */
  recipientEntityId: string;
  /** Optional filter by package type */
  packageType?: string;
  /** Optional title override */
  title?: string;
  /** Optional CSS class name */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface PackageItemProps {
  package_: CorrespondencePackageSchema;
  matterId: string;
}

const PackageItem: React.FC<PackageItemProps> = ({ package_, matterId }) => {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deletePackage, { isLoading: isDeleting }] =
    useCorrespondencePackageDestroyMutation();

  const typeLabel =
    PACKAGE_TYPE_DISPLAY[package_.package_type]?.label || package_.package_type;

  // Determine status config - prioritize transmission status over render status
  const statusConfig = React.useMemo(() => {
    // If package has been transmitted, show transmission status
    const transmissionStatus = package_.message?.transmission_status;
    if (transmissionStatus) {
      return STATUS_DISPLAY[transmissionStatus] ?? STATUS_DISPLAY.pending;
    }

    // Otherwise show render status
    return STATUS_DISPLAY[package_.status || 'draft'] ?? STATUS_DISPLAY.draft;
  }, [package_]);

  const createdAt = package_.created_at
    ? new Date(package_.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Build URL for viewing the package
  const viewUrl = `/matters/${matterId}/correspondence/${package_.id}`;

  // Can only delete if not transmitted
  const canDelete = !package_.is_transmitted;

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePackage({
        id: package_.id,
        matterId,
      }).unwrap();
      toast.success('Deleted');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Failed to delete');
      console.error('Delete error:', error);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Link
          to={viewUrl}
          className="flex flex-1 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
              {package_.package_type === 'lien_notice' ? (
                <Mail className="h-4 w-4 text-gray-600" />
              ) : package_.package_type === 'foia_request' ? (
                <Printer className="h-4 w-4 text-gray-600" />
              ) : (
                <FileText className="h-4 w-4 text-gray-600" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {typeLabel}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    statusConfig.bgColor,
                    statusConfig.color,
                  )}
                >
                  {statusConfig.label}
                </span>
              </div>
              {createdAt && (
                <p className="text-xs text-gray-500 mt-0.5">{createdAt}</p>
              )}
            </div>
          </div>
          <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" />
        </Link>

        {canDelete && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-gray-600 hover:text-red-600" />
          </button>
        )}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {typeLabel.toLowerCase()}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Card that displays correspondence package history for a given entity.
 *
 * Usage:
 * ```tsx
 * <CorrespondenceHistoryCard
 *   matterId={matterId}
 *   recipientEntityType="insurance_carrier"
 *   recipientEntityId={coverage.carrier_v2_id}
 * />
 * ```
 */
export const CorrespondenceHistoryCard: React.FC<
  CorrespondenceHistoryCardProps
> = ({
  matterId,
  recipientEntityType,
  recipientEntityId,
  packageType,
  title = 'Correspondence History',
  className,
}) => {
  // Build query params based on entity type
  const queryParams = React.useMemo(() => {
    const params: {
      matterId: string;
      packageType?: string;
      recipientCarrierId?: string;
      recipientProviderId?: string;
      recipientPoliceDepartmentId?: string;
      recipientPartyId?: string;
    } = { matterId };

    if (packageType) {
      params.packageType = packageType;
    }

    switch (recipientEntityType) {
      case 'insurance_carrier':
        params.recipientCarrierId = recipientEntityId;
        break;
      case 'medical_provider':
        params.recipientProviderId = recipientEntityId;
        break;
      case 'police_department':
        params.recipientPoliceDepartmentId = recipientEntityId;
        break;
      case 'party':
        params.recipientPartyId = recipientEntityId;
        break;
      default:
        break;
    }

    return params;
  }, [matterId, packageType, recipientEntityType, recipientEntityId]);

  const { data: packages, isLoading } = useCorrespondencePackageListQuery(
    queryParams,
    {
      skip: !recipientEntityId,
    },
  );

  // Don't render if no entity ID or loading
  if (!recipientEntityId) {
    return null;
  }

  // Don't render if no packages found (after loading)
  if (!isLoading && (!packages || packages.length === 0)) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-gray-50 p-4',
        className,
      )}
    >
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-600 border-r-transparent" />
        </div>
      ) : (
        <div className="space-y-2">
          {packages?.map((pkg) => (
            <PackageItem key={pkg.id} package_={pkg} matterId={matterId} />
          ))}
        </div>
      )}
    </div>
  );
};
